import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  DestroyRef,
  output
} from '@angular/core';
import { NgClass } from '@angular/common';
import { LotScheduleTaskLookupItem, TaskStatusEnum } from '../../../../../scheduler/src/index';
import { DatePipe } from '@angular/common';
import { LotInfoPopupComponent } from '../lot-info-popup/lot-info-popup.component';
import { ProjectInfoPopupComponent } from '../project-info-popup/project-info-popup.component';
import { FormsModule } from '@angular/forms';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { SchedulerService } from '@zayan-erp/scheduler';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ScheduleVpoComponent } from '../../../../../../apps/cinari-erp-client/src/app/scheduler-module/components/schedule-vpo/schedule-vpo.component';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AbilityService } from '@zayan-erp/shared';
import { NgbModalService } from '@zayan-erp/shared';

@Component({
  selector: 'lot-schedule-task-details',
  standalone: true,
  imports: [NgClass, DatePipe, LotInfoPopupComponent, ProjectInfoPopupComponent, FormsModule, DatePickerModule],
  templateUrl: './lot-schedule-task-details.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class LotScheduleTaskDetailsComponent {
  // Inject services
  #schedulerService = inject(SchedulerService);
  #destroyRef = inject(DestroyRef);
  #modalService = inject(NgbModal);
  // modalOptions = inject(NgbModal);
  _ngbModalService = inject(NgbModalService);
  #abilityService = inject(AbilityService);
  ability = this.#abilityService.ability;

  // Output event to refresh data
  refreshScheduleLotOnTaskUpdate = output<string>();

  // Use required signal-based input
  selectedTask = input.required<LotScheduleTaskLookupItem>();

  // For displaying status appropriately
  hasMilestone = computed(() => this.selectedTask().milestone);
  hasFloatDays = computed(() => this.selectedTask().floatDay > 0);

  // Computed property to determine if unstart button should be disabled
  isUnstartDisabled = computed(() => {
    const task = this.selectedTask();
    // Disable if there's no start date OR if task has both start and end dates
    return !task.actualStartDate || (task.actualStartDate && task.actualEndDate);
  });

  // Date fields
  actualStartDate = signal<Date | null>(null);
  actualEndDate = signal<Date | null>(null);

  // Validation signals
  startDateError = signal<string | null>(null);
  endDateError = signal<string | null>(null);
  exceptionReasonError = signal<string | null>(null);

  // Exception handling
  showExceptionInput = signal<boolean>(false);
  exceptionReason = signal<string>('');

  TaskStatusEnum = TaskStatusEnum;
  constructor() {
    // Initialize date fields when selectedTask changes
    effect(() => {
      const task = this.selectedTask();

      // Reset validation errors
      this.resetValidationErrors();

      // Reset exception input state
      this.showExceptionInput.set(false);
      this.exceptionReason.set(task.exceptionReason || '');

      // Update dates from backend values
      this.actualStartDate.set(task.actualStartDate ? new Date(task.actualStartDate + 'Z') : null);
      this.actualEndDate.set(task.actualEndDate ? new Date(task.actualEndDate + 'Z') : null);
    });
  }

  // Methods for handling user interactions
  toggleExceptionInput() {
    this.showExceptionInput.update(value => !value);
    this.resetValidationErrors();
  }

  resetValidationErrors() {
    this.startDateError.set(null);
    this.endDateError.set(null);
    this.exceptionReasonError.set(null);
  }

  validateDates(): boolean {
    let isValid = true;
    this.resetValidationErrors();

    if (!this.actualStartDate()) {
      this.startDateError.set('Start date is required');
      isValid = false;
    }

    if (!this.actualEndDate()) {
      this.endDateError.set('End date is required');
      isValid = false;
    }

    return isValid;
  }

  validateExceptionSubmit(): boolean {
    const datesValid = this.validateDates();

    if (!this.exceptionReason()) {
      this.exceptionReasonError.set('Exception reason is required');
      return false;
    }

    return datesValid;
  }

  submitException() {
    if (!this.validateExceptionSubmit()) return;

    const taskData = {
      status: TaskStatusEnum.COMPLETED_WITH_EXCEPTION,
      exceptionReason: this.exceptionReason(),
      endDate: this.actualEndDate()?.toLocaleString('sv').replace(' ', 'T'),
      startDate: this.actualStartDate()?.toLocaleString('sv').replace(' ', 'T')
    };

    this.#schedulerService
      .updateScheduleLotTask(this.selectedTask().id, taskData)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.showExceptionInput.set(false);
          this.exceptionReason.set('');
          this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
        }
      });
  }

  markAsCompleted() {
    if (!this.validateDates()) return;

    const taskData = {
      startDate: this.actualStartDate()?.toLocaleString('sv').replace(' ', 'T'),
      endDate: this.actualEndDate()?.toLocaleString('sv').replace(' ', 'T'),
      status: TaskStatusEnum.COMPLETED
    };

    this.#schedulerService
      .updateScheduleLotTask(this.selectedTask().id, taskData)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
        }
      });
  }

  skipTask() {
    // Check if task is already skipped to determine whether to skip or un-skip
    if (this.selectedTask().status === TaskStatusEnum.SKIP) {
      // Call un-skip API
      this.#schedulerService
        .unSkipTask(this.selectedTask().id)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: () => {
            this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
          }
        });
    } else {
      // Call skip API
      const taskData = {
        status: TaskStatusEnum.SKIP
      };

      this.#schedulerService
        .updateScheduleLotTask(this.selectedTask().id, taskData)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: () => {
            this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
          }
        });
    }
  }

  startToday() {
    const taskData = {
      startDateIsToday: true // Only using startDateIsToday as specified
    };

    this.#schedulerService
      .updateScheduleLotTask(this.selectedTask().id, taskData)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
        }
      });
  }

  unstartTask() {
    this.#schedulerService
      .unStartTask(this.selectedTask().id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
        }
      });
  }

  holdTask() {
    this.#schedulerService
      .holdAndUHoldTask(this.selectedTask().id)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe({
        next: () => {
          this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
        }
      });
  }

  updateActualStartDate(date: Date) {
    this.actualStartDate.set(date);
    this.startDateError.set(null);

    // When start date changes, call API with only startDate in body
    if (date) {
      const taskData = {
        startDate: date.toLocaleString('sv').replace(' ', 'T')
      };

      this.#schedulerService
        .updateScheduleLotTask(this.selectedTask().id, taskData)
        .pipe(takeUntilDestroyed(this.#destroyRef))
        .subscribe({
          next: () => {
            this.refreshScheduleLotOnTaskUpdate.emit(this.selectedTask().id);
          }
        });
    }
  }

  updateActualEndDate(date: Date) {
    this.actualEndDate.set(date);
  }

  updateExceptionReason(reason: string) {
    this.exceptionReason.set(reason);
    this.exceptionReasonError.set(null);
  }

  openScheduleVpoModal(): void {
    const modalRef = this.#modalService.open(ScheduleVpoComponent, this._ngbModalService.rightModal);
    modalRef.componentInstance.scheduleLotTaskId = this.selectedTask().id;
  }
}
